import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useShellSearchParams } from '../lib/use-shell-search-params';
import { useLocale } from '../i18n/locale-context';
import {
  fetchCategories,
  searchIkpu,
  resolveNiche,
  parseKindParam,
  type CatalogFilters,
  parseStatusParam,
  parseIntParam,
  parseIsoDateParam,
} from '../lib/catalog-client';
import { SubjectPicker, type SubjectSelection } from './ui/subject-picker';
import { ikpuWordsSearchParams } from '../lib/ikpu-search-fallback';

/**
 * Activation line: a single "niche" combobox — one search over the broad
 * categories, the curated Niche layer (Spec #3), AND the granular ИКПУ
 * taxonomy — paired with the save-search action. Category, Niche, and ИКПУ
 * are the same single axis (broad → curated → precise); picking one clears
 * the other two, so a saved search always carries exactly one of them.
 * The refinement filters (Region / Status / Sort / Budget / Deadline) + export
 * live in {@link CatalogFilters}.
 *
 * The combobox itself (trigger, dropdown, search, the three result sections,
 * browse/search modes, count rendering) is `ui/subject-picker.tsx`
 * ({@link SubjectPicker}) — extracted 2026-08 so the saved-search builder
 * could reuse it. Everything URL-related (params, the category/ikpu/niche
 * deep-link resolve, mutual exclusion) stays here.
 *
 * - `highlightCategory`: draw attention to the niche control (activation nudge).
 * - `trailing`: the "Сохранить поиск" node, placed beside the niche control.
 * - `nudge`: the activation banner, stuck directly above the line.
 * - `emphasize`: wrap the line in a standout card while activation isn't done.
 * - `spotlight`: the unfiltered catalogue's first ask, for a guest or an account
 *   with no saved search (Igor, 06.10) — the line becomes a highlighted card
 *   headed «Выберите, что вы поставляете — покажем подходящие тендеры». The
 *   field's own orange ring (`highlightCategory`) and «Сохранить поиск» stay
 *   inside it. Static text, so it prerenders as-is.
 * - `spotlightPending`: see the prop — the spotlight while auth is unknown.
 */
export function FilterBar({
  highlightCategory = false,
  emphasize = false,
  spotlight = false,
  spotlightPending = false,
  trailing,
  nudge,
  scope,
  status: statusOverride,
  awarded,
}: {
  highlightCategory?: boolean;
  emphasize?: boolean;
  spotlight?: boolean;
  /** The session is still being read (`authStatus === 'loading'`): the card is
   *  rendered as before — the prerender is a guest's page and hydration must
   *  match it — but marked, so CSS can keep it invisible in a browser whose
   *  `uzt_authed` cookie says it is signed in (globals.css, index.html). */
  spotlightPending?: boolean;
  trailing?: ReactNode;
  nudge?: ReactNode;
  /** Fixed catalog layer of the page (e.g. 'ifi'), so the category / niche /
   *  ИКПУ option counts are computed in the layer the viewer is actually
   *  browsing. */
  scope?: string;
  /** Status the PAGE imposes regardless of the URL — `/results` is the awarded
   *  archive, which is a route, not a query param the user set. Without it the
   *  option counts answer for the open catalogue while the list below shows the
   *  archive. */
  status?: string;
  /** Same, for the awarded-outcome axis. */
  awarded?: boolean;
}) {
  const { locale, t } = useLocale();
  const [params, setParams] = useShellSearchParams();

  const category = params.get('category') ?? '';
  const ikpu = params.get('ikpu') ?? '';
  const niche = params.get('niche') ?? '';

  /**
   * The refinement filters a pick in this panel KEEPS — region, kind, status,
   * budget, deadline, "new since", and the page's fixed layer. ALL THREE lists
   * (category · niche · ИКПУ) count their options under exactly these, so the
   * number on a row is the number of results clicking it yields. `q` is absent
   * by design: every pick clears it (see the three `select*` handlers below);
   * the niche axis is absent because the pick replaces it.
   */
  const countFilters = useMemo<CatalogFilters>(() => {
    // Shared with catalog.tsx / kind-tabs / ifi-source-tabs so every surface that
    // sends these params applies the SAME rule. The local version here only
    // checked `Number.isFinite`, which still let a negative through — the API's
    // `@Min(0)` then 400s the counts request.
    const num = (key: string): number | undefined => {
      const guarded = parseIntParam(params.get(key));
      return guarded === undefined ? undefined : Number(guarded);
    };
    return {
      scope: scope || undefined,
      region: params.get('region') || undefined,
      // Guard, not a cast — `kind` is user-editable and the API 400s anything
      // outside the three literals (see parseKindParam).
      kind: parseKindParam(params.get('kind')),
      status: statusOverride ?? parseStatusParam(params.get('status')),
      awarded,
      minBudget: num('minBudget'),
      maxBudget: num('maxBudget'),
      deadlineFrom: parseIsoDateParam(params.get('deadlineFrom')),
      deadlineTo: parseIsoDateParam(params.get('deadlineTo')),
      since: parseIsoDateParam(params.get('since')),
    };
  }, [params, scope, statusOverride, awarded]);

  const { data: categories } = useQuery({
    queryKey: ['catalog', 'categories', locale, countFilters],
    queryFn: () => fetchCategories(locale, countFilters),
  });

  // Local mirror of the selected ИКПУ so we can show its NAME (the URL only
  // carries the code). Narrower than `IkpuItem` on purpose — this component
  // only ever reads `.code`/`.name` back off it; the rest of the shape
  // (count, level, …) belongs to the picker that fetched it.
  const [ikpuSel, setIkpuSel] = useState<Array<{ code: string; name: string }>>([]);
  // Local mirror of the selected Niche so we can show its localized NAME (the URL
  // only carries the slug). A fresh page load with `?niche=` resolves the slug to
  // its name via `resolveNiche` below (the niche mirror of ИКПУ's on-load resolve).
  const [nicheSel, setNicheSel] = useState<Array<{ slug: string; name: string }>>([]);

  // Category, Niche, and ИКПУ are ONE axis — selecting any one clears the
  // other two, so a saved search always carries exactly one of them.
  function selectCategory(slug: string) {
    const next = new URLSearchParams(params);
    if (slug) {
      next.set('category', slug);
      next.delete('ikpu');
      next.delete('niche');
      // Same rule as `selectIkpu`: picking a classifier is a different intent
      // from searching words, and the count on the row was computed WITHOUT
      // `q`. Keeping it would hand back an empty page right after the option
      // promised N results. Only a real pick clears it — «Все» (slug === '')
      // just drops the classifier and leaves the caller's search alone.
      next.delete('q');
    } else {
      next.delete('category');
      next.delete('ikpu');
      next.delete('niche');
    }
    next.delete('page');
    setParams(next);
    if (!slug) setIkpuSel([]);
    setNicheSel([]);
  }

  function selectIkpu(code: string, name: string) {
    const next = new URLSearchParams(params);
    next.set('ikpu', code);
    next.delete('category');
    next.delete('niche');
    next.delete('page');
    // Picking a code is a DIFFERENT intent from searching words, and the two
    // sets rarely intersect ("доставка сотрудников" hits a training course and
    // a boarding school; 49.39 holds passenger transport). Keeping both would
    // hand back an empty page right after the option promised N results — so
    // the pick clears `q`, and the count is computed without it to match.
    next.delete('q');
    setParams(next);
    setIkpuSel([{ code, name }]);
    setNicheSel([]);
  }

  // Selecting a curated Niche (Spec #3) sets `niche=<slug>` and clears both
  // `category` and `ikpu` — the mutual-exclusion rule above, applied in the
  // other direction.
  function selectNiche(slug: string, name: string) {
    const next = new URLSearchParams(params);
    next.set('niche', slug);
    next.delete('category');
    next.delete('ikpu');
    next.delete('page');
    // …and clears `q`, for the same reason `selectIkpu` does (see there).
    next.delete('q');
    setParams(next);
    setNicheSel([{ slug, name }]);
    setIkpuSel([]);
  }

  // No code answers what was typed: the words go into the page's own search,
  // exactly as the header box submits them (`ikpuWordsSearchParams`) — same
  // page, the box shows them, the subject axis they replace is cleared.
  function searchWords(q: string) {
    setParams(ikpuWordsSearchParams(q, params));
    setIkpuSel([]);
    setNicheSel([]);
  }

  // Deep-link: resolve a `?ikpu=<code>` present on load to its full item so the
  // trigger shows the niche name, not a bare code.
  const { data: ikpuResolved } = useQuery({
    queryKey: ['ikpu-resolve', ikpu, locale],
    queryFn: () => searchIkpu(ikpu, locale),
    enabled: !!ikpu && ikpuSel[0]?.code !== ikpu,
  });
  useEffect(() => {
    if (!ikpu) {
      if (ikpuSel.length) setIkpuSel([]);
      return;
    }
    if (ikpuSel[0]?.code === ikpu) return;
    const match = ikpuResolved?.find((r) => r.code === ikpu);
    if (match) setIkpuSel([match]);
  }, [ikpu, ikpuResolved, ikpuSel]);

  // Deep-link: resolve a `?niche=<slug>` present on load to its full item so the
  // trigger shows the localized niche name, not a prettified slug (mirrors the
  // ИКПУ resolve above).
  const { data: nicheResolved } = useQuery({
    queryKey: ['niche-resolve', niche, locale],
    queryFn: () => resolveNiche(niche, locale),
    enabled: !!niche && nicheSel[0]?.slug !== niche,
  });
  useEffect(() => {
    if (!niche) {
      if (nicheSel.length) setNicheSel([]);
      return;
    }
    if (nicheSel[0]?.slug === niche) return;
    if (nicheResolved && nicheResolved.slug === niche) setNicheSel([nicheResolved]);
    else if (nicheSel.length) setNicheSel([]);
  }, [niche, nicheResolved, nicheSel]);

  // The picker's whole selection state, boiled down to the one axis that's
  // actually active (category XOR ikpu XOR niche — enforced by the three
  // `select*` functions above). Priority mirrors the old inline label:
  // category, then ikpu, then niche — the only order that matters in
  // practice, since only one of the three is ever non-empty at a time.
  const selected: SubjectSelection[] = useMemo(() => {
    if (category) {
      // ⚠️ `category` can be a COMMA UNION, not one slug. `buildWhere` has
      // always read it that way, and a saved search carrying several
      // categories arrives here as one — `savedSearchToCatalogPath` joins them.
      // Looking up the whole string as a single slug found nothing, so the
      // trigger fell back to printing the raw param:
      // «design-engineering,construction,energy-fuel».
      //
      // ⚠️ …but a SLUG CAN ITSELF CONTAIN A COMMA. `groupItDigital` folds the
      // `it-telecom` + `telecom-equipment` rows into one synthetic option whose
      // slug is literally `it-telecom,telecom-equipment` and whose members are
      // REMOVED from the list. So splitting first and resolving the parts finds
      // neither of them and prints «it-telecom, telecom-equipment» — which is
      // exactly what shipped, and what this whole block was meant to prevent.
      //
      // Resolve the WHOLE string first: it wins for the grouped option and for
      // any ordinary single slug. Only fall back to splitting when nothing
      // matches, which is the genuine multi-category union.
      const byExactSlug = (categories ?? []).find((c) => c.slug === category);
      if (byExactSlug) {
        return [{ kind: 'category' as const, slug: category, name: byExactSlug.name }];
      }
      const slugs = category.split(',').filter(Boolean);
      const nameOf = (slug: string) =>
        (categories ?? []).find((c) => c.slug === slug)?.name ?? slug;
      return slugs.map((slug) => ({ kind: 'category' as const, slug, name: nameOf(slug) }));
    }
    if (ikpu) return [{ kind: 'ikpu', code: ikpu, name: ikpuSel[0]?.name ?? ikpu }];
    if (niche)
      return [{ kind: 'niche', slug: niche, name: nicheSel[0]?.name ?? niche.replace(/-/g, ' ') }];
    return [];
  }, [category, ikpu, niche, categories, ikpuSel, nicheSel]);

  return (
    <div
      className={
        spotlight
          ? 'flex flex-col gap-3 rounded-xl border-2 border-orange/50 bg-orange/10 p-4 shadow-m-sm md:max-w-5xl md:p-5'
          : emphasize
            ? 'flex flex-col gap-3 rounded-xl border border-orange/25 bg-orange/5 p-4 md:max-w-5xl'
            : 'flex flex-col gap-2 md:max-w-5xl'
      }
      // See `spotlightPending`: globals.css hides the card behind this marker
      // in a browser with a session hint, and React never reads the hint.
      data-spotlight-pending={spotlight && spotlightPending ? '' : undefined}
    >
      {/* No emoji: old Android fonts and headless renderers draw a box. */}
      {spotlight && (
        <p
          data-spotlight-title
          className="font-brand text-[16px] font-extrabold leading-snug tracking-[-0.2px] text-ink md:text-[18px]"
        >
          {t('catalog.spotlight.title')}
        </p>
      )}
      {nudge}
      {/* Same 4-column module as the filter grid below: the niche spans the first
          two columns (over Region · Status) and the save action takes the third
          (over Sort), so the block still reads as one aligned grid — but the niche
          field keeps a comfortable width instead of stretching across three cells. */}
      <div className="flex flex-col gap-2.5 md:grid md:grid-cols-4 md:items-center md:gap-3">
        {/* One niche combobox: category OR ИКПУ, searched together. */}
        <SubjectPicker
          mode="single"
          selected={selected}
          onSelect={(s) => {
            if (s.kind === 'category') selectCategory(s.slug);
            else if (s.kind === 'ikpu') selectIkpu(s.code, s.name);
            else selectNiche(s.slug, s.name);
          }}
          onClear={() => selectCategory('')}
          countFilters={countFilters}
          highlight={highlightCategory}
          onSearchWords={searchWords}
          className="md:col-span-2"
        />
        {trailing && <div className="w-full md:col-span-1">{trailing}</div>}
      </div>
    </div>
  );
}
